/** Seed default for PlatformConfig.ipnKeyword. The live value comes from the
 *  database — read it with getIpnKeyword() in apps/api, never import this
 *  constant to build a QR. */
export const RECONCILE_CONTENT_KEYWORD = 'LOPHOC';

/**
 * The exact bank-transfer memo content for a tuition invoice — QR addInfo AND
 * the text a payer copies must both use this, or a manual transfer skips
 * SePay's whitelist and never auto-reconciles.
 *
 * `keyword` is required and has no default on purpose: a default would let a
 * stale call site keep compiling while silently stamping the wrong keyword.
 * Requiring it makes the compiler enumerate every site that needs the live value.
 *
 * Subscription billing (LopHoc → teacher, platform master account) is a separate
 * SePay connection and must NOT use this — see buildVietQRUrlFromBin in apps/api.
 */
export function buildTransferContent(paymentRef: string, keyword: string): string {
  return `${keyword} ${paymentRef}`;
}

export type PlanCode = 'FREE' | 'PLUS' | 'PRO' | 'CENTER';

/** Display prices for the purchasable plans, mirrored by seedPlans.ts — the
 *  API test `seedPlans.test.ts` asserts they stay equal, so a repricing that
 *  touches one without the other fails CI instead of showing a stale amount
 *  on a payment QR screen. */
export const PLAN_PRICES_VND = {
  PLUS:   { monthly: 149_000, annual: 1_428_000 },
  PRO:    { monthly: 499_000, annual: 4_788_000 },
  CENTER: { monthly: 990_000, annual: 9_480_000 },
} as const;

export interface Plan {
  id: string;
  code: PlanCode;
  name: string;
  priceVnd: number;
  /** Annual list price ≈ monthly − 20%: monthly × 0.8 rounded to a clean
   *  thousand, then × 12 (Johnny 2026-08-26). It was "10 × monthly / tặng 2
   *  tháng" until then; the 10× rule produced unreadable per-month equivalents
   *  (332.500đ) once annual mode started leading with the per-month figure.
   *  The rounding is per-plan, not a formula applied uniformly: Center's
   *  monthly × 0.8 is 792k, which rounds DOWN to 790k, giving 9.480.000 rather
   *  than 9.504.000 — seedPlans.ts pins the three totals for this reason.
   *  0 means the plan is not sold annually. Numbers live in seedPlans.ts. */
  priceVndAnnual: number;
  maxInvoicesPerMonth: number;
  maxClients: number;
  maxStudents: number;
  maxAiMessagesPerMonth: number;
  maxReconcileTxPerMonth: number;
  maxTeacherSeats: number;
  showsWatermark: boolean;
  allowsExtras: boolean;
  allowsBookkeeping: boolean;
  allowsAiImport: boolean;
  allowsPayroll: boolean;
  /** The RIGHT to share an invoice over Zalo via the manual send-zalo button
   *  (Pro/Center) — renamed from allowsZnsAutoSend (THI-179): no auto-send
   *  path exists. Volume is a separate prepaid top-up, never part of the
   *  flat subscription bill (THI-74). */
  allowsZaloSend: boolean;
  /** THI-137: the right to ADD a branch (Center). Existing branches keep
   *  working and filtering on any plan — the gate never strands shipped data. */
  allowsBranches: boolean;
  /** THI-105: the right to create a Sổ liên lạc (parent portal) link for a
   *  student — Pro/Center. Exposed on `Plan` (not just the server's
   *  `PlanFeature` map) so the dashboard can gate the GET on this flag
   *  client-side, same as `allowsBookkeeping`/`allowsBranches` — otherwise
   *  the query has to fire and 403 to learn the answer, which also trips
   *  `fetchApi`'s shared upgrade-dialog side effect on a passive read. */
  allowsProgressLink: boolean;
}

/**
 * The per-month figure an annual price is *displayed* as (the "Suno pattern").
 *
 * Printing the yearly total as the headline makes the cheapest tool in the
 * market look like the most expensive one, so annual mode shows this number big
 * and the true yearly total in a small line beneath it — nothing is hidden,
 * because the amount actually charged is always printed right there.
 *
 * Rounded DOWN to the nearest 500đ. Since the −20% repricing (2026-08-26) every
 * shipped annual total is 12 × a clean thousand (1.428.000 / 4.788.000 /
 * 9.480.000 — Pro went 3.828.000 → 4.788.000 on 2026-09-05, THI-199), so the
 * rounding is a **no-op for the current ladder**. The
 * function stays because it is the display contract, not arithmetic: whatever a
 * future annual price is, the per-month figure shown must never be larger than
 * the real one.
 */
export function perMonthFromAnnualVnd(annualVnd: number): number {
  return Math.floor(annualVnd / 12 / 500) * 500;
}

/**
 * How far ahead of expiry the subscription tick raises the renewal invoice —
 * and the same window the dashboard nudge opens in, so the two can never drift
 * apart (a nudge window wider than the issuance lead points at an invoice that
 * does not exist yet).
 *
 * It lives here, not in apps/api, because the billing page now prints the date
 * it implies ("Ngày {periodEnd − 7} LopHoc tạo hoá đơn kỳ tới…") and a
 * dashboard cannot import from the API. `subscriptionBilling.ts` re-exports it
 * as RENEWAL_LEAD_DAYS, so there is still exactly one number.
 *
 * Paying early costs the teacher nothing: applyPayment stacks the new period on
 * top of the remaining one rather than replacing it.
 */
export const SUBSCRIPTION_RENEWAL_LEAD_DAYS = 7;

export interface Subscription {
  id: string;
  organizationId: string;
  planId: string;
  status: 'TRIALING' | 'ACTIVE' | 'CANCELLED' | 'PAST_DUE' | 'EXPIRED';
  startedAt: string;
  endedAt: string | null;
}

export interface OrgSubscriptionStatus {
  plan: Plan;
  subscription: Subscription;
  usage: {
    students: number;
    aiCreditsUsed: number;
    aiCreditsLimit: number;
  };
  zaloSendEnabled: boolean;
}

/**
 * Why a prorated upgrade's story is FROZEN onto the invoice instead of derived
 * when someone opens the order page.
 *
 * `computeUpgradeProration` reads the subscription's OLD plan, OLD cycle,
 * `currentPeriodEnd` and `now`. The invoice row stores none of them — it stores
 * the TARGET plan and cycle — and `applyPayment` overwrites all three on the
 * subscription the instant the invoice is paid. The order page is also the
 * receipt page, so a read-time recomputation would print the new plan as the
 * old one on the very first view. Renewals and later upgrades move the period
 * end again. There is no reconstruction: for a cross-cycle upgrade the credit
 * leaves no trace in any stored field at all.
 *
 * Written once at creation, never read to decide money. Null on every invoice
 * that was not prorated (renewals, trial purchases, FREE → paid), which is
 * exactly the set that must show no breakdown.
 */
export type SubscriptionProrationBreakdown =
  | {
      kind: 'SAME_CYCLE';
      /** The plan being left, by name as it read at the time of quoting. */
      fromPlanName: string;
      toPlanName: string;
      cycle: 'MONTHLY' | 'ANNUAL';
      /** Full-price difference per cycle: target price − current price. */
      diffVnd: number;
      /** 30 for MONTHLY, 365 for ANNUAL — the denominator the rate line prints. */
      cycleDays: number;
      /**
       * Whole days left in the running period. CAN EXCEED `cycleDays` — an
       * admin-comped org sits on a MONTHLY cycle with a year-long period, and
       * the charge is deliberately uncapped (see `computeUpgradeProration`).
       * That is the case this line exists to explain.
       */
      remainingDays: number;
      /**
       * Equals the invoice's `amountVnd` BEFORE any promotion discount —
       * i.e. `promotionSnapshot.listAmountVnd` when a promotion applied
       * (THI-200: the discount applies to this prorated amount, never to
       * the plan price, so this field and `invoice.amountVnd` diverge on
       * a discounted invoice by design). Equals `invoice.amountVnd`
       * exactly when there is no promotion. Pinned by a test.
       */
      amountVnd: number;
    }
  | {
      kind: 'CROSS_CYCLE';
      fromPlanName: string;
      toPlanName: string;
      fromCycle: 'MONTHLY' | 'ANNUAL';
      toCycle: 'MONTHLY' | 'ANNUAL';
      /** The target plan's full list price for the new term. */
      fullPriceVnd: number;
      /**
       * The EFFECTIVE credit — `fullPriceVnd - amountVnd`, not the raw
       * fractional remainder. `amountVnd` is floored to a whole thousand, so
       * the raw credit would print a subtraction that misses the charged total
       * by up to 999đ. Defined as the difference, the two lines reconcile by
       * construction.
       */
      creditVnd: number;
      /**
       * Equals the invoice's `amountVnd` BEFORE any promotion discount —
       * i.e. `promotionSnapshot.listAmountVnd` when a promotion applied
       * (THI-200: the discount applies to this prorated amount, never to
       * the plan price, so this field and `invoice.amountVnd` diverge on
       * a discounted invoice by design). Equals `invoice.amountVnd`
       * exactly when there is no promotion. Pinned by a test.
       */
      amountVnd: number;
    };

/**
 * Bank-transfer friendly: round down to whole thousands, never below 1.000đ.
 *
 * Moved here from `apps/api/src/services/subscriptionBilling.ts` (THI-200,
 * INVARIANTS §1.5) — proration math and promotion math both floor to a whole
 * thousand and must floor the same way, so there is exactly one
 * implementation in the repo. Never redefine this locally; import it.
 */
export function roundVndDown(n: number): number {
  return Math.max(1000, Math.floor(n / 1000) * 1000);
}

/**
 * The single amount/discount split for a percent-off promotion — used by the
 * API (to price the invoice), the dashboard (to preview it before checkout)
 * and the landing page (to advertise it), so all three print the same two
 * numbers for the same input (THI-200, INVARIANTS §1.5).
 *
 * `discountVnd` is defined as `listAmountVnd - amountVnd`, a subtraction,
 * rather than computed as its own percentage of the list price. Both figures
 * are shown together on the invoice ("Giá gốc: X, Giảm giá: −Y, Còn lại: Z"),
 * and `amountVnd` is floored to a whole thousand while a raw
 * `listAmountVnd * percentOff / 100` is not — computing `discountVnd`
 * independently would print two lines that fail to add up by up to 999đ
 * (INVARIANTS §1.7: the two displayed lines must balance to the đồng).
 * Defining it as the difference makes them reconcile by construction.
 *
 * The `Math.min` guard exists so a list amount at or below the 1.000đ floor
 * that `roundVndDown` enforces can never produce a charge LARGER than the
 * list amount (e.g. 1.000đ at 90% off floors the raw 100đ up to the 1.000đ
 * minimum, which would otherwise exceed the list price and make the
 * "discount" negative) — with the guard, `discountVnd` can never go
 * negative.
 */
export function applyPercentOff(
  listAmountVnd: number,
  percentOff: number,
): { amountVnd: number; discountVnd: number } {
  const amountVnd = Math.min(listAmountVnd, roundVndDown((listAmountVnd * (100 - percentOff)) / 100));
  const discountVnd = listAmountVnd - amountVnd;
  return { amountVnd, discountVnd };
}

export type PromotionKind = 'PERCENT_OFF';

export type PromoCodeReason =
  | 'INVALID'
  | 'EXPIRED'
  | 'NOT_STARTED'
  | 'PLAN'
  | 'CYCLE'
  | 'ALREADY_USED'
  | 'CAP'
  | 'STATUS';

export interface Promotion {
  id: string;
  code: string | null;
  name: string;
  kind: PromotionKind;
  percentOff: number;
  startsAt: string;
  endsAt: string;
  planCodes: PlanCode[];
  appliesToMonthly: boolean;
  appliesToAnnual: boolean;
  active: boolean;
  ownerLabel: string | null;
  maxRedemptions: number | null;
  note: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PromotionSnapshot {
  promotionId: string;
  code: string | null;
  name: string;
  percentOff: number;
  listAmountVnd: number;
  discountVnd: number;
}

export interface SubscriptionOffer {
  promotionId: string;
  name: string;
  /** THI-200 follow-up: the marketing headline, or `null` to fall back to the
   *  generated sentence ("Ưu đãi {name}: giảm {pct}% kỳ đầu cho mọi gói"). */
  headline: string | null;
  percentOff: number;
  /** ISO instant: the org's OWN deadline (campaign end, or trial end under §2.5.2). */
  deadline: string;
  planCodes: PlanCode[];
  cycles: Array<'MONTHLY' | 'ANNUAL'>;
}

export interface PublicPromotion {
  name: string;
  /** THI-200 follow-up: the marketing headline, or `null` to fall back to the
   *  generated sentence (`formatPromotionSentence` in apps/web). */
  headline: string | null;
  percentOff: number;
  endsAt: string;
  planCodes: PlanCode[];
  cycles: Array<'MONTHLY' | 'ANNUAL'>;
}

/**
 * THI-200 follow-up — the shared countdown math for the landing announcement
 * bar and the in-app offer banner: both tick a "days + live hh:mm:ss" clock
 * against the same deadline, and both must agree on what "0 left" means
 * (clamped, never negative). Each app owns its own Vietnamese formatter (the
 * two apps have different i18n setups) — this function computes only the
 * numbers, never a string.
 */
export function countdownParts(
  deadlineIso: string,
  now: Date,
): { days: number; hours: number; minutes: number; seconds: number; totalMs: number } {
  const totalMs = Math.max(0, new Date(deadlineIso).getTime() - now.getTime());
  const totalSeconds = Math.floor(totalMs / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds, totalMs };
}

/**
 * THI-200 fix wave: a code is checked in the context of ONE plan/cycle (the
 * preview route's query params), but the compare table shows every
 * purchasable tier at once.
 *
 * `planCodes`/`cycles` are the CODE'S OWN coverage — NOT the best-of winner's
 * (fix wave 2: populating them from the winner was a regression. A code that
 * covers only PRO would report the CAMPAIGN's wider coverage whenever the
 * campaign outranked it, so the client — which by then sent the code
 * unconditionally — advertised a plan the code itself does not cover and
 * then 400'd buying it instead of falling back to the campaign the org was
 * ALSO eligible for). The client uses `planCodes`/`cycles` to decide whether
 * to send the code at all for a given plan/cycle: sending it only when the
 * CODE covers that combination, and omitting it otherwise so the server can
 * still apply an eligible campaign.
 *
 * `percentOff` is the WINNER's (campaign or code, whichever `findOfferForOrg`'s
 * best-of picked, §2.4) — what would actually be charged if this exact
 * plan/cycle were bought right now. `codePercentOff` is the CODE's OWN
 * percentage, distinct from `percentOff` when the campaign beats the code —
 * without it, PromoCodeRow has no way to tell the customer "your code is
 * still 30%, we just applied the campaign's 40% instead" (see B7).
 */
export type PromoCodeCheck =
  | {
      valid: true;
      name: string;
      percentOff: number;
      codePercentOff: number;
      listAmountVnd: number;
      amountVnd: number;
      discountVnd: number;
      planCodes: PlanCode[];
      cycles: Array<'MONTHLY' | 'ANNUAL'>;
    }
  | { valid: false; reason: PromoCodeReason };
